import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { TutorTrace } from "./tutorTrace";

export type OutputLine =
  | { type: "stdout"; text: string }
  | { type: "stderr"; text: string }
  | { type: "error";  text: string };

/** Élément affiché dans l'onglet « Graphique » de la console.
 *  - `media` : ancien format mono-frame (SVG turtle/gamelib, PNG, JPEG).
 *  - `canvas` : Sprint 5.A — placeholder pour un <canvas> dont le contrôle est
 *    transféré au worker via `transferControlToOffscreen()`. Le worker dessine
 *    directement dessus en canvas2D. En 5.A aucun `display_canvas_request` n'est
 *    émis (gamelib reste sur SVG pour `affiche()`) — la branche est testée mais
 *    pas déclenchée par les leçons existantes. */
export type DisplayItem =
  | { kind: "media";  mime: string; data: string }
  | { kind: "canvas"; canvasId: string; cssWidth: number; cssHeight: number };

// Résultats de l'intelligence IDE (Jedi, dans le worker).
export type HoverInfo    = { doc: string; name: string; type: string };
export type GotoLocation = { line: number; column: number }; // convention Jedi (col 0-based)

// Plafond de défilement (type terminal) : borne mémoire/DOM si un programme imprime
// énormément (ex. boucle infinie). On rogne les lignes les plus anciennes au-delà du
// budget et on garde un marqueur de troncature en tête.
const MAX_OUTPUT_CHARS = 200_000;
const TRUNC_LINE: OutputLine = { type: "stdout", text: "… (début de sortie tronqué)\n" };

function appendCapped(prev: OutputLine[], line: OutputLine): OutputLine[] {
  // Repart sans le marqueur existant pour recalculer proprement.
  const base = prev.length > 0 && prev[0] === TRUNC_LINE ? prev.slice(1) : prev;
  const next = [...base, line];
  let total = 0;
  for (const l of next) total += l.text.length;
  if (total <= MAX_OUTPUT_CHARS) return next;
  while (next.length > 1 && total > MAX_OUTPUT_CHARS) {
    total -= next[0].text.length;
    next.shift();
  }
  return [TRUNC_LINE, ...next];
}

/** Code prof injecté autour du code élève à l'exécution (cf. plan gamelib P0).
 *  Composition : `pre + code élève + post` → le worker exécute l'ensemble. La
 *  variable spéciale `__student_code__` reste exposée séparément pour analyse AST
 *  côté `test_code` (cf. python.worker.js). */
export interface Scaffold {
  pre:  string;
  post: string;
  /** Pack de jeu optionnel — monté dans `/lib/pymods/<name>/` avant exécution
   *  pour que le scaffold puisse faire `import <name>` (cf. plan gamelib P1.5). */
  pack?: { id?: number; name: string; files: Record<string, string>; assets_manifest?: Record<string, string> };
}

/** Phases d'exécution envoyées au worker (Sprint 2.B) — chacune exec'd
 *  séparément pour filtrer les tracebacks (le pre/post est masqué à l'élève).
 *  Remplace l'ancien composeWithScaffold qui concaténait tout. */
interface RunPhases {
  pre?: string;
  userCode?: string;
  post?: string;
  testCode?: string;
}

export interface KernelState {
  ready:        boolean;
  running:      boolean;
  error:        string | null;
  output:       OutputLine[];
  displays:     DisplayItem[];
  inputRequest: boolean;
  visualizing:  boolean;
  run:          (code: string, scaffold?: Scaffold) => Promise<void>;
  runTest:      (userCode: string, testCode: string, scaffold?: Scaffold) => Promise<boolean>;
  visualize:    (code: string, inputs?: string[]) => Promise<TutorTrace>;
  signature:    (code: string, line: number, column: number) => Promise<HoverInfo | null>;
  goto:         (code: string, line: number, column: number) => Promise<GotoLocation | null>;
  interrupt:    () => void;
  submitInput:  (text: string) => void;
  putFile:      (name: string, content: ArrayBuffer) => void;
  clearOutput:  () => void;
  /** Sprint 5.A — transfère le contrôle d'un <canvas> HTML au worker via
   *  OffscreenCanvas. Doit n'être appelé QU'UNE FOIS par élément <canvas> (la
   *  méthode `transferControlToOffscreen` l'interdit deux fois). Le composant
   *  appelant doit garder un drapeau (ex: `canvas.dataset.plTransferred`) pour
   *  l'idempotence en React 18 Strict Mode. */
  attachCanvas: (canvas: HTMLCanvasElement, cssWidth: number, cssHeight: number) => void;
  /** Sprint 5.A — relâche le canvas attaché côté worker. À appeler au
   *  démontage du composant qui détient le `<canvas>`. */
  detachCanvas: () => void;
  /** Sprint 6 — poste un évènement clavier/souris au worker. Appelé par
   *  CanvasBlock depuis ses listeners DOM (keydown/keyup/click/blur). */
  sendInputEvent: (data: InputEvent) => void;
}

/** Sprint 6 — évènement d'entrée envoyé du main au worker. */
export type InputEvent =
  | { kind: "keydown"; key: string }
  | { kind: "keyup";   key: string }
  | { kind: "click";   x: number; y: number; button: number }
  | { kind: "blur" };

// Disposition du SharedArrayBuffer partagé avec le worker (voir python.worker.js) :
//   ctrl[0] : drapeau (0 = worker en attente, 1 = saisie prête)
//   ctrl[1] : longueur en octets de la chaîne
//   octets à partir de DATA_OFFSET : la chaîne UTF-8
const SAB_DATA_OFFSET = 8;

const KernelContext = createContext<KernelState | null>(null);

type PendingRun = { resolve: () => void; hadError: boolean };

export function KernelProvider({ children }: { children: ReactNode }) {
  const workerRef   = useRef<Worker | null>(null);
  const pendingRef  = useRef<Map<string, PendingRun>>(new Map());
  // Promesses en attente d'une trace de visualisation (résolues au message 'trace').
  const tracePendingRef = useRef<Map<string, { resolve: (t: TutorTrace) => void; reject: (e: Error) => void }>>(new Map());
  // Promesses en attente d'une requête Jedi (signature/goto), résolues par id avec le
  // résultat JSON (ou null). Une requête jamais résolue (kernel occupé) reste juste en
  // attente ; les fournisseurs Monaco la rejouent au survol/clic suivant.
  const jediPendingRef = useRef<Map<string, (result: string | null) => void>>(new Map());
  // Vues sur le SharedArrayBuffer partagé avec le worker (pour input()).
  const ctrlRef     = useRef<Int32Array | null>(null);
  const dataRef     = useRef<Uint8Array | null>(null);
  // Le worker est-il actuellement bloqué sur un input() non répondu ?
  const inputPendingRef = useRef(false);
  // Buffer d'interruption partagé (bouton Stop) : 2 = SIGINT.
  const interruptRef    = useRef<Uint8Array | null>(null);

  const [ready, setReady]             = useState(false);
  const [running, setRunning]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [output, setOutput]           = useState<OutputLine[]>([]);
  const [displays, setDisplays]       = useState<DisplayItem[]>([]);
  const [inputRequest, setInputRequest] = useState(false);
  const [visualizing, setVisualizing]   = useState(false);

  useEffect(() => {
    // Classic worker servi depuis public/ → origin localhost:5173 → same-origin COEP OK
    const worker = new Worker("/python.worker.js");
    workerRef.current = worker;

    worker.onmessage = (event) => {
      const msg = event.data as {
        type: string;
        text?: string;
        id?: string;
        sab?: SharedArrayBuffer;
        mime?: string;
        data?: string;
        trace?: string;
        error?: string;
        name?: string;
        result?: string | null;
        // Sprint 5.A : champs canvas
        cssWidth?: number;
        cssHeight?: number;
      };

      switch (msg.type) {
        case "sab":
          if (msg.sab) {
            ctrlRef.current = new Int32Array(msg.sab, 0, 2);
            dataRef.current = new Uint8Array(msg.sab, SAB_DATA_OFFSET);
          }
          break;

        case "interrupt_sab":
          if (msg.sab) interruptRef.current = new Uint8Array(msg.sab);
          break;

        case "ready":
          setReady(true);
          break;

        case "error":
          setError(msg.text ?? "Erreur inconnue");
          break;

        case "input_request":
          inputPendingRef.current = true;
          setInputRequest(true);
          break;

        case "display":
          setDisplays((p) => [...p, {
            kind: "media", mime: msg.mime ?? "", data: msg.data ?? "",
          }]);
          break;

        case "display_canvas_request": {
          // Sprint 5.A — le worker demande un <canvas> HTML.
          // Le composant DisplayBlock détectera ce kind et fera le
          // transferControlToOffscreen. cssWidth/cssHeight viennent par défaut
          // de _scene["width"]/"height"; on peut overrider depuis le worker.
          //
          // Sprint 5.B fix : quand l'animation démarre (boucle()), le canvas
          // remplace TOUT le contenu de l'onglet Graphique (les `affiche()`
          // SVG du scaffold_pre étaient juste un état initial ; à partir de la
          // boucle, le canvas devient la source de vérité visuelle).
          const cssW = Number(msg.cssWidth) || 320;
          const cssH = Number(msg.cssHeight) || 180;
          setDisplays(() => [
            { kind: "canvas", canvasId: msg.id ?? "default", cssWidth: cssW, cssHeight: cssH },
          ]);
          break;
        }

        case "canvas_attached":
        case "canvas_unsupported":
        case "canvas_already_attached":
          // Acks informatifs du worker (cf. attachCanvas). En 5.A on log
          // seulement pour debug ; le Sprint 5.B exposera ces états à l'UI
          // pour un fallback gracieux si le canvas n'a pas pu être attaché.
          if (msg.type !== "canvas_attached") {
            console.warn("[kernel] canvas attach:", msg.type);
          }
          break;

        case "stdout":
          setOutput((p) => appendCapped(p, { type: "stdout", text: msg.text ?? "" }));
          break;

        case "stderr":
          setOutput((p) => appendCapped(p, { type: "stderr", text: msg.text ?? "" }));
          break;

        case "fileWritten":
          setOutput((p) => appendCapped(p, {
            type: "stdout",
            text: `Fichier importé : ${msg.name} — ouvre-le avec open(${JSON.stringify(msg.name)}).\n`,
          }));
          break;

        case "fileError":
          setOutput((p) => appendCapped(p, {
            type: "stderr",
            text: `Échec de l'import de « ${msg.name} » : ${msg.text}\n`,
          }));
          break;

        case "pyerror": {
          setOutput((p) => [...p, { type: "error", text: msg.text ?? "" }]);
          // Marque l'exécution en cours comme échouée (pour runTest)
          if (msg.id) {
            const pending = pendingRef.current.get(msg.id);
            if (pending) pending.hadError = true;
          }
          break;
        }

        case "signature":
        case "goto": {
          if (!msg.id) break;
          const resolve = jediPendingRef.current.get(msg.id);
          if (resolve) {
            jediPendingRef.current.delete(msg.id);
            resolve(msg.result ?? null);
          }
          break;
        }

        case "trace": {
          setVisualizing(false);
          if (!msg.id) break;
          const pending = tracePendingRef.current.get(msg.id);
          if (!pending) break;
          tracePendingRef.current.delete(msg.id);
          if (msg.error) {
            pending.reject(new Error(msg.error));
          } else {
            try {
              pending.resolve(JSON.parse(msg.trace ?? "{}") as TutorTrace);
            } catch (e) {
              pending.reject(e instanceof Error ? e : new Error(String(e)));
            }
          }
          break;
        }

        case "done": {
          inputPendingRef.current = false;
          setRunning(false);
          setInputRequest(false);
          if (msg.id) {
            const pending = pendingRef.current.get(msg.id);
            if (pending) {
              pendingRef.current.delete(msg.id);
              pending.resolve();
            }
          }
          break;
        }
      }
    };

    worker.onerror = (e) => {
      setError(`Erreur worker : ${e.message}`);
      console.error("[KernelContext] worker error:", e);
    };

    worker.postMessage({ action: "init" });

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  const _exec = useCallback((
    phases: RunPhases,
    pack?: { id?: number; name: string; files: Record<string, string>; assets_manifest?: Record<string, string> },
    runMode: "play" | "test" = "play",
  ): Promise<{ hadError: boolean }> => {
    const worker = workerRef.current;
    if (!worker) return Promise.resolve({ hadError: true });

    setRunning(true);
    const id = Math.random().toString(36).slice(2);

    return new Promise((resolve) => {
      const pending: PendingRun = {
        hadError: false,
        resolve: () => resolve({ hadError: pending.hadError }),
      };
      pendingRef.current.set(id, pending);
      // Le worker exécute pre/user/post/test séparément (cf. python.worker.js),
      // ce qui permet de masquer les tracebacks scaffold sans leak (bug T3).
      // studentCode (= userCode) reste exposé sous __student_code__ pour les
      // analyses AST côté test_code, comme avant.
      // Sprint 5.B : `runMode` permet à gamelib.boucle() de choisir entre
      // boucle infinie ("play", bouton Exécuter) et auto-timeout 3s ("test",
      // bouton Valider) — sans que le scaffold prof ait à wrapper.
      worker.postMessage({
        action: "run", id,
        pre: phases.pre ?? "", userCode: phases.userCode ?? "",
        post: phases.post ?? "", testCode: phases.testCode ?? "",
        studentCode: phases.userCode ?? "",
        pack, runMode,
      });
    });
  }, []);

  const run = useCallback(async (code: string, scaffold?: Scaffold) => {
    await _exec({
      pre:      scaffold?.pre,
      userCode: code,
      post:     scaffold?.post,
    }, scaffold?.pack, "play");
  }, [_exec]);

  const runTest = useCallback(async (
    userCode: string,
    testCode: string,
    scaffold?: Scaffold,
  ): Promise<boolean> => {
    // Sprint 2.B : envoie chaque phase séparément ; le worker exec dans
    // l'ordre pre → user → post → test. Le test_code s'exécute APRÈS le
    // scaffold post pour observer l'état final (cf. plan gamelib P0).
    const { hadError } = await _exec({
      pre:      scaffold?.pre,
      userCode,
      post:     scaffold?.post,
      testCode,
    }, scaffold?.pack, "test");
    return !hadError;
  }, [_exec]);

  // Génère une trace PythonTutor pour le visualiseur (n'exécute pas dans la console).
  // `inputs` : file des saisies input() déjà collectées (pour la ré-exécution
  // progressive quand le code en demande davantage — cf. CodeWorkspace).
  const visualize = useCallback((code: string, inputs: string[] = []): Promise<TutorTrace> => {
    const worker = workerRef.current;
    if (!worker) return Promise.reject(new Error("Kernel indisponible."));
    setVisualizing(true);
    const id = Math.random().toString(36).slice(2);
    return new Promise<TutorTrace>((resolve, reject) => {
      tracePendingRef.current.set(id, { resolve, reject });
      worker.postMessage({ action: "visualize", id, code, inputs });
    });
  }, []);

  // Intelligence IDE (Jedi) : envoie une requête au worker et résout avec le résultat
  // analysé. `code` complet + position en convention Jedi (ligne 1-based, col 0-based).
  const jediQuery = useCallback((action: "signature" | "goto", code: string, line: number, column: number): Promise<string | null> => {
    const worker = workerRef.current;
    if (!worker) return Promise.resolve(null);
    const id = Math.random().toString(36).slice(2);
    return new Promise<string | null>((resolve) => {
      jediPendingRef.current.set(id, resolve);
      worker.postMessage({ action, id, code, line, column });
    });
  }, []);

  const signature = useCallback(async (code: string, line: number, column: number): Promise<HoverInfo | null> => {
    const res = await jediQuery("signature", code, line, column);
    if (!res) return null;
    try { return JSON.parse(res) as HoverInfo; } catch { return null; }
  }, [jediQuery]);

  const goto = useCallback(async (code: string, line: number, column: number): Promise<GotoLocation | null> => {
    const res = await jediQuery("goto", code, line, column);
    if (!res) return null;
    try { return JSON.parse(res) as GotoLocation; } catch { return null; }
  }, [jediQuery]);

  // Réponse à un input() : écrit la chaîne dans le buffer partagé puis réveille
  // le worker bloqué sur Atomics.wait.
  const submitInput = useCallback((text: string) => {
    const ctrl = ctrlRef.current;
    const data = dataRef.current;
    if (!ctrl || !data) return;
    const bytes = new TextEncoder().encode(text);
    const len   = Math.min(bytes.length, data.length);
    data.set(bytes.subarray(0, len));
    Atomics.store(ctrl, 1, len);   // longueur
    Atomics.store(ctrl, 0, 1);     // drapeau « saisie prête »
    Atomics.notify(ctrl, 0, 1);    // réveille le worker
    inputPendingRef.current = false;
    setInputRequest(false);

    // Écho de la saisie dans la console (comme un vrai terminal) : on l'accole à
    // la ligne du prompt si celle-ci n'est pas terminée, sinon on ajoute une ligne.
    setOutput((p) => {
      const next = [...p];
      const last = next[next.length - 1];
      if (last && last.type === "stdout" && !last.text.endsWith("\n")) {
        next[next.length - 1] = { ...last, text: last.text + text + "\n" };
      } else {
        next.push({ type: "stdout", text: text + "\n" });
      }
      return next;
    });
  }, []);

  // Bouton Stop : lève SIGINT côté Pyodide (KeyboardInterrupt). Si le worker est
  // bloqué sur un input() en attente, on débloque aussi Atomics.wait pour que
  // l'interruption soit effectivement prise en compte.
  const interrupt = useCallback(() => {
    if (interruptRef.current) interruptRef.current[0] = 2; // SIGINT
    if (inputPendingRef.current) {
      const ctrl = ctrlRef.current;
      if (ctrl) {
        Atomics.store(ctrl, 1, 0);
        Atomics.store(ctrl, 0, 1);
        Atomics.notify(ctrl, 0, 1);
      }
      inputPendingRef.current = false;
      setInputRequest(false);
    }
  }, []);

  // Écrit un fichier (téléversé par l'élève) dans le FS virtuel Pyodide. Le code
  // peut ensuite l'ouvrir via open(name). Un accusé (fileWritten/fileError) revient
  // sous forme de ligne dans la console.
  const putFile = useCallback((name: string, content: ArrayBuffer) => {
    workerRef.current?.postMessage({ action: "putFile", name, content });
  }, []);

  // Sprint 5.A — attache un <canvas> HTML au worker via OffscreenCanvas.
  const attachCanvas = useCallback((canvas: HTMLCanvasElement, cssW: number, cssH: number) => {
    const w = workerRef.current;
    if (!w) return;
    if (typeof canvas.transferControlToOffscreen !== "function") {
      // Safari < 16.4 et autres anciens browsers : pas de support → le worker
      // restera en mode fallback SVG (gamelib._scene["mode"] === "svg").
      console.warn("[kernel] transferControlToOffscreen non supporté — mode SVG.");
      return;
    }
    let offscreen: OffscreenCanvas;
    try { offscreen = canvas.transferControlToOffscreen(); }
    catch (e) {
      // Probable cause : canvas déjà transféré (Strict Mode remount sans cleanup).
      // L'appelant doit avoir vérifié `canvas.dataset.plTransferred` avant.
      console.warn("[kernel] transferControlToOffscreen a échoué:", e);
      return;
    }
    const ratio = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
    w.postMessage(
      { action: "attachCanvas", offscreen, pixelRatio: ratio, cssWidth: cssW, cssHeight: cssH },
      [offscreen],
    );
  }, []);

  const detachCanvas = useCallback(() => {
    workerRef.current?.postMessage({ action: "detachCanvas" });
  }, []);

  // Sprint 6 — post unique chemin pour tous les évènements DOM input.
  const sendInputEvent = useCallback((data: InputEvent) => {
    workerRef.current?.postMessage({ action: "input_event", ...data });
  }, []);

  const clearOutput = useCallback(() => {
    // Sprint 5.A — reset canvas AVANT toute notification SIGINT, pour que la
    // frame d'animation en vol (Sprint 5.B) ne dessine pas après le reset.
    // L'ordre est critique : `resetCanvas` incrémente `animGen` côté worker →
    // la prochaine résolution du setTimeout vérifiera `gen !== animGen` et
    // sortira sans dessiner.
    workerRef.current?.postMessage({ action: "resetCanvas" });
    // Si le worker est bloqué sur un input() non répondu, on le débloque (ligne
    // vide) avant tout : sinon le kernel reste gelé (running coincé à true) lors
    // d'un nouveau run, d'un « Effacer » ou d'un changement d'étape.
    if (inputPendingRef.current) {
      const ctrl = ctrlRef.current;
      if (ctrl) {
        Atomics.store(ctrl, 1, 0);  // longueur 0 → input() renvoie ""
        Atomics.store(ctrl, 0, 1);
        Atomics.notify(ctrl, 0, 1);
      }
      inputPendingRef.current = false;
    }
    setOutput([]);
    setDisplays([]);
    setInputRequest(false);
  }, []);

  return (
    <KernelContext.Provider
      value={{ ready, running, error, output, displays, inputRequest, visualizing, run, runTest, visualize, signature, goto, interrupt, submitInput, putFile, clearOutput, attachCanvas, detachCanvas, sendInputEvent }}
    >
      {children}
    </KernelContext.Provider>
  );
}

export function useKernelContext(): KernelState {
  const ctx = useContext(KernelContext);
  if (!ctx) throw new Error("useKernelContext must be inside KernelProvider");
  return ctx;
}
